import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ACCESS_TOKEN } from 'utils/storage-keys';
import { getItem, getSiteCode } from 'utils/local-storage';
import env from 'utils/env';

import { Col, Row } from 'antd';
import PremiumFeature, { Benefit } from './blocks/PackageBenefit';
import UpgradePopup from 'components/Popup/UpgradePopup';
import Loading from 'components/Loading/Loading';

import IconStart from '../../../assets/img/icon-stars.svg';
import QR from '../../../assets/img/open-app-qr.jpg';
import './UpgradePackage.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetUserProfileApi } from '../user/useUserApi';

type Props = {
  upgradeType?: 'elsa_speak' | 'premium' | 'professional';
};

type CatalogVariant = {
  base_price: number;
  price: number;
  discount_percent: number;
  interface: {
    package: string;
    package_name: string;
  };
};

type CatalogResponse = {
  code: 'elsa_speak' | 'premium' | 'ai_role_play_sa' | 'nova_premium' | 'professional' | 'bundle_elsa_professional';
  name: string;
  photo_url: string;
  benefits: Benefit[];
  variants: CatalogVariant[];
};

type PackageInfo = {
  membership: string;
  package_name: string;
  base_price: number;
  discount_price: number;
  discount_percent: number;
};

type UserInfo = {
  total: number;
  metadata: {
    code: string;
    expire_at: number;
  };
};

type UpgradeInfo = {
  name: string;
  photo: string;
  packages: PackageInfo[];
  benefits: Benefit[];
};

const UpgradePackage: React.FC<Props> = ({ upgradeType = 'premium' }) => {
  const config = env();
  const token = getItem(ACCESS_TOKEN);
  const siteCode = getSiteCode();
  const navigate = useNavigate();
  const { isLoading: profileLoading } = useGetUserProfileApi();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    total: 0,
    metadata: {
      code: 'free',
      expire_at: 0
    }
  });
  const [showPopup, setShowPopup] = useState(false);
  const [membership, setMembership] = useState('');
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeInfo>({
    photo: '',
    name: '',
    benefits: [],
    packages: []
  });
  // The balance note names the tier being bought, so it can't say Premium on every page.
  const upgradeTierName =
    upgradeType === 'premium'
      ? 'Premium'
      : upgradeType === 'professional'
        ? 'Professional'
        : 'Pro';
  const isProLifetime =
    userInfo.metadata.code === 'pro' && !userInfo.metadata.expire_at;
  // What the checkout charges: the catalog price minus the balance it deducts.
  // A balance at or above the price gets no upgrade price; checkout refuses that order.
  const getUpgradePrice = (price: number) =>
    userInfo.total > 0 && userInfo.total < price ? price - userInfo.total : null;

  const { t } = useTranslation();

  useEffect(() => {
    if (!token || profileLoading) return;
    setLoading(true);

    const requestConfig = {
      headers: {
        'X-Session-Token': token
      }
    };

    const fetchData = async () => {
      try {
        const [unusedMoney, catalog] = await Promise.all([
          axios.get(
            `${config.backendPaymentApiUrl}/account/get-unused-money?currency=vnd&site_code=${siteCode}`,
            requestConfig
          ),
          axios.get(
            `${config.baseApiUrl}/product-catalog/v1/site/${siteCode}/upgrade?country=VN`,
            requestConfig
          )
        ]);
        setUserInfo({
          total: unusedMoney.data?.total || 0,
          metadata: unusedMoney.data?.metadata || {
            code: 'free',
            expire_at: 0
          }
        });
        const catalogRes: CatalogResponse[] = catalog.data?.catalogs || [];
        if (!catalogRes.length) {
          setLoading(false);
          return setUpgradeInfo({
            photo: '',
            name: '',
            benefits: [],
            packages: []
          });
        }
        // Professional is sold under two catalog codes: the influencer campaign
        // sites use bundle_elsa_professional, the regular sites use professional.
        // A visitor arriving from a campaign LP carries its site-code, so accept both.
        const wantedCodes =
          upgradeType === 'professional'
            ? ['professional', 'bundle_elsa_professional']
            : [upgradeType];
        const packages = catalogRes.find((item) =>
          wantedCodes.includes(item.code)
        );
        if (!packages) {
          setLoading(false);
          return setUpgradeInfo({
            photo: '',
            name: '',
            benefits: [],
            packages: []
          });
        }

        const packagesInfo: PackageInfo[] = packages?.variants.map((item) => ({
          membership: item?.interface?.package,
          package_name: item?.interface?.package_name,
          base_price: item?.base_price,
          discount_price: item?.price,
          discount_percent: item?.discount_percent
        }));

        setUpgradeInfo({
          name: packages?.name || '',
          photo: packages?.photo_url || '',
          benefits: packages?.benefits || [],
          packages: packagesInfo
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, siteCode, config, profileLoading, upgradeType]);

  useEffect(() => {
    if (userInfo.metadata.code === 'premium' && upgradeType !== 'professional') {
      navigate('/');
    }
  }, [userInfo, navigate, upgradeType]);

  const handleSubmit = () => {
    setShowPopup(true);
  };

  return (
    <div className="upgrade-premium">
      {loading ? (
        <Row justify="center">
          <Loading color="#ccc" />
        </Row>
      ) : (
        <Row>
          <Col md={12} span={24}>
            <div className="upgrade-premium__current">
              <h3 className="title">{t('courses.current_package')}</h3>
              <div className="package">
                <p className={`package-name ${userInfo.metadata.code}`}>
                  ELSA{' '}
                  {userInfo.metadata.code
                    ? userInfo.metadata.code[0].toUpperCase() +
                      userInfo.metadata.code.substring(1)
                    : ''}
                </p>
                <div className="next-bill">
                  {isProLifetime ? (
                    <strong>{t('packages.lifetime')}</strong>
                  ) : userInfo.metadata.expire_at ? (
                    <>
                      <p>{t('courses.expired_date')}:</p>
                      <strong>
                        {new Date(
                          userInfo.metadata.expire_at * 1000
                        ).toLocaleDateString('en-GB')}
                      </strong>
                    </>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </div>
            <div className="upgrade-premium__upgrade">
              <h3 className="title">
                {t('courses.upgrade_to') +
                  (upgradeType === 'premium' ? ' Premium' : upgradeType === 'professional' ? ' Professional' : ' Pro')}
              </h3>
              {userInfo.metadata.code === 'premium' &&
              upgradeType !== 'professional' ? (
                <div className="note">
                  <p>{t('courses.scan_qr_code')}</p>
                  <img src={QR} alt="" width={240} />
                </div>
              ) : !upgradeInfo.packages.length ? (
                <p className="note">{t('courses.upgrade_not_available')}</p>
              ) : (
                upgradeInfo.packages.length && (
                  <>
                    <div className="package-list">
                      {upgradeInfo.packages.map((item) => (
                        <label
                          className="package"
                          htmlFor={item.membership}
                          key={item.membership}
                        >
                          {item.membership.includes('lifetime_membership') && (
                            <p className="head">
                              <img src={IconStart} alt="" />{' '}
                              {t('courses.conversation_bundle_info')}
                            </p>
                          )}
                          <div className="content">
                            <input
                              type="radio"
                              name="membership_name"
                              value={item.membership}
                              id={item.membership}
                              onChange={(e) => setMembership(e.target.value)}
                              checked={membership === item.membership}
                              aria-label={item.package_name}
                            />
                            <p className="package-name">{item.package_name}</p>
                            {getUpgradePrice(item.discount_price) !== null ? (
                              <p className="price">
                                <span className="price-original">
                                  {item.discount_price.toLocaleString('it-IT')}đ
                                </span>
                                {' → '}
                                {getUpgradePrice(item.discount_price)?.toLocaleString('it-IT')}đ
                              </p>
                            ) : (
                              <>
                                {!!item.discount_percent && (
                                  <div className="discount">
                                    -{item.discount_percent}%
                                  </div>
                                )}
                                <p className="price">
                                  {item.discount_price.toLocaleString('it-IT')}đ
                                </p>
                              </>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    {!!userInfo.total && (
                      <div className="upgrade-premium__balance">
                        Số dư {userInfo.total.toLocaleString('it-IT')} đ còn lại
                        của bạn sẽ được khấu trừ khi nâng cấp lên gói{' '}
                        {upgradeTierName}.
                      </div>
                    )}
                    <button
                      className="btn-primary"
                      onClick={handleSubmit}
                      disabled={!membership}
                    >
                      {t('continue')}
                    </button>
                  </>
                )
              )}
            </div>
          </Col>
          {userInfo.metadata.code !== 'premium' && (
            <Col md={12} span={24}>
              <PremiumFeature
                photo={upgradeInfo.photo}
                benefit={upgradeInfo.benefits || []}
                title={`${upgradeInfo.name} Benefits:`}
              />
            </Col>
          )}
        </Row>
      )}
      {showPopup && (
        <UpgradePopup
          membership={membership}
          money={userInfo.total || 0}
          siteCode={siteCode}
          setShowPopup={setShowPopup}
        />
      )}
    </div>
  );
};

export default UpgradePackage;
